import { useState, useEffect } from 'react';
import { Typography } from '@mui/joy';
import axios from '@app/config/axios.config';

interface ClubNameDisplayProps {
    mandatRef: string;
    showRef?: boolean; // Afficher aussi la référence
    separator?: string; // Séparateur entre ref et nom
    color?: 'primary' | 'neutral' | 'danger' | 'success' | 'warning';
    level?:
        | 'h1'
        | 'h2'
        | 'h3'
        | 'h4'
        | 'title-lg'
        | 'title-md'
        | 'title-sm'
        | 'body-lg'
        | 'body-md'
        | 'body-sm';
}

const AppClubNameDisplay = ({
    mandatRef,
    showRef = true,
    separator = ' - ',
    color = 'neutral',
    level = 'body-sm',
}: ClubNameDisplayProps) => {
    const [clubName, setClubName] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);

    const fetchClubName = async (mandatRef: string) => {
        // Créer la promesse de requête
        const mandatRefString = mandatRef.toString();

        try {
            const params = {
                page: 0,
                limit: 1,
                search: '',
                sort_by: '',
                asc: true,
                filters: [
                    {
                        filter_by: 'ref',
                        filter_type: 'is_equal',
                        filter_value: mandatRefString, // Forcer comme string
                    },
                ],
                secondarySearch: '',
            };

            const response = await axios.get('/mandats', { params });
            const mandat = response.data?.result?.[0];

            if (mandat?.club?.nom) {
                setClubName(mandat.club.nom);
            }
        } catch (error) {
            console.error(
                `Erreur lors de la récupération du club pour ${mandatRef}:`,
                error
            );
        }
    };

    useEffect(() => {
        if (!mandatRef) {
            setClubName('');
            return;
        }

        // Sinon, récupérer via API
        setLoading(true);
        fetchClubName(mandatRef).then(() => {
            setLoading(false);
        });
    }, [mandatRef]);

    // Construire le texte à afficher
    const displayText = () => {
        if (loading) {
            return showRef ? mandatRef : '...';
        }

        if (!clubName) {
            return showRef ? mandatRef : '—';
        }

        if (showRef) {
            return `${mandatRef}${separator}(${clubName})`;
        }

        return clubName;
    };

    return (
        <Typography level={level} color={color}>
            {displayText()}
        </Typography>
    );
};

export default AppClubNameDisplay;
